export interface SignalConfig {
	funcId: string;
	rowKey: string;
	signalId: string;
	inputs: string;
	initSchedule?: string;
	runSchedule?: string;
	isEnabled?: boolean;
	venue?: string;
	mode?: string;
}

export interface SignalMessage {
	config: SignalConfig;
	data: string;
}
export interface SignalObservation {
	signalId: string;
	observedAt: string;
	sessionDate: string;
	runKind: RunKind;
	isPublished: boolean;
	signalData: string;
	scheduleToken?: string | null;
	funcId?: string;
	correlationId?: string | null;
	createdAt?: string;
	// Bar provenance, on AUDIT rows only: bare-STJ text (PascalCase keys), parsed by the page.
	provenance?: string | null;
}

// A string-literal union, not a TS enum — enums are reserved here for numeric-wire types. All
// members are declared even though /latest can only return the first three: the interface is
// named SignalObservation, so it reads as the wire vocabulary rather than one view's subset, and
// a TS union over HTTP data is a compile-time-only claim that would under-declare with no signal
// if this type were later reused for /observations. Any style or label map over it needs an
// explicit neutral branch.
export type RunKind =
	| 'CLOSE'
	| 'SCHEDULED'
	| 'MANUAL'
	| 'ON_DEMAND'
	| 'BACKFILL'
	| 'AUDIT'
	| 'UNKNOWN';

// String unions with a catch-all: every classification on the audit wire is a plain string, and
// a member added server-side must render (the warn chip with the raw string), never miss.
export type AuditVerdict =
	| 'MATCH'
	| 'MATCH_WARN'
	| 'MISMATCH'
	| 'ENGINE_DIVERGED'
	| 'NO_BASELINE'
	| 'NO_AUDIT'
	| 'UNREADABLE'
	| (string & {});
export type AuditState = 'PENDING' | 'NOT_SCHEDULED' | 'MISSED' | 'FAILED' | (string & {});
export type AuditFailureReason =
	| 'XF_DAILY_UNAVAILABLE'
	| 'ARCHIVE_MISSING'
	| 'INTRADAY_INCOMPLETE'
	| 'RUN_FAILED'
	| (string & {});
export type AuditUnreadableSide = 'BASELINE' | 'AUDIT' | 'BOTH' | (string & {});
export type AuditUnreadableReason = 'DECLINED' | 'NO_POSITIVE_READ' | 'MALFORMED' | (string & {});

export interface AuditFieldComparison {
	name: string;
	baseline: string; // JSON text of the value
	audit: string;
	compared: boolean;
	equal: boolean;
}

export interface SignalAuditComparison {
	signalId: string;
	sessionDate: string;
	verdict: AuditVerdict;
	auditState: AuditState | null;
	failureReason: AuditFailureReason | null;
	unreadableSide: AuditUnreadableSide | null;
	unreadableReason: AuditUnreadableReason | null;
	baseline: SignalObservation | null;
	audit: SignalObservation | null;
	fields: AuditFieldComparison[];
	comparatorVersion: number;
	leverageTolerance: number;
}

// Mirror of Data.Signal's AuditProvenance: PascalCase on purpose — it is JSON.parsed from the
// provenance string, and the camelCase HTTP envelope never reaches inside that string.
export interface AuditProvenanceDaily {
	Provider: string;
	Symbol: string;
	Count: number;
	First: string | null;
	Last: string | null;
	DayD: { Open: number; High: number; Low: number; Close: number } | null;
}
export interface AuditProvenanceIntraday {
	Provider: string;
	Symbol: string;
	From: string;
	To: string;
	Count: number;
}
export interface AuditProvenance {
	Daily: AuditProvenanceDaily | null;
	Intraday: AuditProvenanceIntraday | null;
	RunInstant: string;
	Trigger: string;
	Attempt: number;
	EventDate: string | null;
	Failure: { Reason: string; Detail: string } | null;
}

export interface SignalOverride {
	signalId: string;
	fundId: string;
	brokerId: string;
	brokerAccountId: string;
	strategyId: string;
	rangeStart: string; // yyyy-MM-dd, inclusive
	rangeEnd: string; // yyyy-MM-dd, inclusive
	min: number;
	max: number;
}

/** The seven fields Core.Trader deletes by. */
export type SignalOverrideIdentity = Omit<SignalOverride, 'min' | 'max'>;

/** Core.Trader's wire serializer drops Min/Max when they equal 0 (WhenWritingDefault), so the
 *  JSON key is absent rather than `0` — normalize at the API boundary so every consumer sees a
 *  real number. */
export const normalizeOverride = (o: SignalOverride): SignalOverride => ({
	...o,
	min: o.min ?? 0,
	max: o.max ?? 0
});

export interface EditSignalOverrides {
	rows: SignalOverride[];
	remove: SignalOverrideIdentity[];
}
